// apps/web/src/components/HelpCenter.js
import React, { useState } from 'react';

/**
 * HelpCenter - Public-facing documentation for customers
 * Accessible via /help route
 */
export default function HelpCenter() {
  const [activeDoc, setActiveDoc] = useState('quickstart');

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Sidebar Navigation */}
      <div style={{ width: 280, backgroundColor: 'white', borderRight: '1px solid #e2e8f0', padding: '24px 0' }}>
        <div style={{ padding: '0 24px', marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Help Centre</h1>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Learn how to use Sentia Tasks</p>
        </div>

        <nav>
          <button
            onClick={() => setActiveDoc('quickstart')}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeDoc === 'quickstart' ? '#eff6ff' : 'transparent',
              color: activeDoc === 'quickstart' ? '#1e40af' : '#475569',
              fontSize: 14,
              fontWeight: activeDoc === 'quickstart' ? 600 : 400,
              cursor: 'pointer',
              borderLeft: activeDoc === 'quickstart' ? '3px solid #3b82f6' : '3px solid transparent',
            }}
          >
            🚀 Quick Start Guide
          </button>

          <button
            onClick={() => setActiveDoc('manual')}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeDoc === 'manual' ? '#eff6ff' : 'transparent',
              color: activeDoc === 'manual' ? '#1e40af' : '#475569',
              fontSize: 14,
              fontWeight: activeDoc === 'manual' ? 600 : 400,
              cursor: 'pointer',
              borderLeft: activeDoc === 'manual' ? '3px solid #3b82f6' : '3px solid transparent',
            }}
          >
            📖 Complete User Manual
          </button>
        </nav>

        <div style={{ padding: '24px', marginTop: 32, borderTop: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Need more help?</p>
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
            <strong>Email:</strong> support@sentia-app.com
          </p>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            Response within 24 hours
          </p>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 48 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {activeDoc === 'quickstart' ? <QuickStartGuide /> : <UserManual />}
        </div>
      </div>
    </div>
  );
}

/**
 * Quick Start Guide Content
 */
function QuickStartGuide() {
  return (
    <div className="prose">
      <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 16 }}>Sentia Tasks: Quick Start Guide</h1>
      <p style={{ fontSize: 18, color: '#64748b', marginBottom: 48 }}>Get up and running in 5 minutes</p>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          Welcome to Sentia Tasks
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Sentia Tasks is a collaborative task management platform designed to help you organise projects, coordinate with your team, and boost productivity. This guide will get you started quickly.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 1: Create Your First Workspace</h2>

        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, marginBottom: 20, border: '1px solid #bfdbfe' }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>What is a workspace?</h3>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            A workspace is a container for your projects. Think of it as a folder that holds multiple lists (projects).
          </p>
        </div>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>To create a workspace:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Log in to Sentia Tasks</li>
          <li>Click the <strong>"+ New Workspace"</strong> button in the sidebar</li>
          <li>Enter a name (e.g., "University Projects", "Marketing Team", "Personal Tasks")</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Your workspace appears in the left sidebar. You can create multiple workspaces to separate different areas of your life or work.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 2: Create Your First List</h2>

        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, marginBottom: 20, border: '1px solid #bfdbfe' }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>What is a list?</h3>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            A list represents a single project within a workspace. Each list contains tasks organised into three columns: To Do, Doing, and Done.
          </p>
        </div>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>To create a list:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Select your workspace from the sidebar</li>
          <li>Click <strong>"+ New List"</strong> at the bottom of the lists panel</li>
          <li>Enter a name (e.g., "Essay Research", "Q1 Campaign", "Weekly Goals")</li>
          <li>Press <strong>Enter</strong> or click outside the field</li>
        </ol>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Your new list appears and opens automatically, showing an empty kanban board.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 3: Add Your First Tasks</h2>

        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, marginBottom: 20, border: '1px solid #bfdbfe' }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>What is a task?</h3>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            A task is a single item of work. Tasks move across three columns as you work on them.
          </p>
        </div>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>To create a task:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Click the <strong>"+ Add Task"</strong> button in the <strong>To Do</strong> column</li>
          <li>Enter a task title (e.g., "Research topic overview")</li>
          <li>Press <strong>Enter</strong></li>
        </ol>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 16 }}>
          The task appears as a card in the To Do column.
        </p>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>To add details to a task:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Click on the task card</li>
          <li>A modal opens where you can:
            <ul style={{ marginTop: 8 }}>
              <li>Edit the title</li>
              <li>Add a detailed description (supports Markdown formatting)</li>
              <li>Add checklists for subtasks</li>
              <li>View creation date and author</li>
            </ul>
          </li>
        </ol>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 24 }}>To move a task:</h4>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Simply drag and drop the task card between columns:
        </p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>To Do</strong> → Tasks you haven't started yet</li>
          <li><strong>Doing</strong> → Tasks you're currently working on</li>
          <li><strong>Done</strong> → Completed tasks</li>
        </ul>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 4: Collaborate with Your Team</h2>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Invite collaborators to your list:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Open the list you want to share</li>
          <li>Click the <strong>"👥 Collaborators"</strong> button in the top-right</li>
          <li>Enter a teammate's email address</li>
          <li>Select their permission level:
            <ul style={{ marginTop: 8 }}>
              <li><strong>View:</strong> Can see tasks but cannot edit</li>
              <li><strong>Edit:</strong> Can create, modify, and delete tasks</li>
            </ul>
          </li>
          <li>Click <strong>"Send Invite"</strong></li>
        </ol>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Your teammate receives an email invitation. Once they accept, they'll see the list in their workspace and can collaborate in real-time.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 5: Use Templates to Save Time</h2>

        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, marginBottom: 20, border: '1px solid #bfdbfe' }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>What are templates?</h3>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Templates are pre-built task lists for common workflows. They help you get started quickly without creating tasks from scratch.
          </p>
        </div>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>To browse templates:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Open a list where you want to add tasks</li>
          <li>Click the <strong>"📋 Templates"</strong> button in the toolbar</li>
          <li>Browse available templates by category:
            <ul style={{ marginTop: 8 }}>
              <li>General (universal workflows)</li>
              <li>Academic (essays, projects, research)</li>
              <li>Work (meetings, reports, launches)</li>
              <li>Personal (goals, habits, planning)</li>
              <li>Marketing, Development, Design</li>
            </ul>
          </li>
        </ol>

        <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 24 }}>To apply a template:</h4>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Select a template from the gallery</li>
          <li>Review the preview to see what tasks it includes</li>
          <li>Click <strong>"Use this template"</strong></li>
          <li>If the template is <strong>interactive</strong>, you'll be prompted to fill in details:
            <ul style={{ marginTop: 8 }}>
              <li>Project name</li>
              <li>Deadlines</li>
              <li>Team size</li>
              <li>Budget</li>
              <li>Platform choices</li>
            </ul>
          </li>
          <li>Click <strong>"Deploy"</strong></li>
        </ol>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          The template creates multiple tasks in your list, customised with your inputs. You can then edit or delete tasks as needed.
        </p>

        <div style={{ backgroundColor: '#fef3c7', padding: 20, borderRadius: 8, marginTop: 24, border: '1px solid #fde047' }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#854d0e' }}>Free vs Premium templates:</h4>
          <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li><strong>Free templates:</strong> Available to everyone, cover basic workflows</li>
            <li><strong>Premium templates:</strong> One-time purchase (£7.99-£49.99), advanced workflows with interactive customisation, unlimited reuse after purchase</li>
          </ul>
        </div>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 6: Work with Checklists</h2>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 16 }}>
          Checklists help you break down complex tasks into smaller steps.
        </p>

        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Open a task by clicking on it</li>
          <li>In the description field, use Markdown syntax:</li>
        </ol>

        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8, marginTop: 16, fontFamily: 'monospace', fontSize: 14 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`### Checklist
- [ ] First step
- [ ] Second step
- [ ] Third step`}</pre>
        </div>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginTop: 16 }}>
          The checklist appears as interactive checkboxes. Click to mark items complete.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Step 7: Stay Synchronised in Real-Time</h2>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 16 }}>
          Sentia Tasks updates automatically when teammates make changes:
        </p>

        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>New tasks appear instantly</strong> when a collaborator adds them</li>
          <li><strong>Task movements</strong> (between columns) sync immediately</li>
          <li><strong>Changes to task details</strong> update for everyone in real-time</li>
          <li><strong>No need to refresh</strong> the page</li>
        </ul>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginTop: 16 }}>
          If you're working offline or lose connection, changes sync automatically when you reconnect.
        </p>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Quick Tips for Productivity</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Organise Your Workspace</h3>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Create separate workspaces for different contexts</li>
              <li>Use descriptive names for lists</li>
              <li>Archive or delete old lists to keep your sidebar clean</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Best Practices for Task Titles</h3>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Keep titles short and actionable</li>
              <li>Start with a verb (Write, Review, Create)</li>
              <li>Avoid vague titles ("Do stuff" or "Work on project")</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Collaborate Effectively</h3>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Use task descriptions to add context</li>
              <li>Add checklists to break down complex tasks</li>
              <li>Review the "Done" column weekly</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Templates Save Time</h3>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Browse the template marketplace regularly</li>
              <li>Purchase premium templates for specialised workflows</li>
              <li>Deploy the same template multiple times</li>
            </ul>
          </div>
        </div>
      </section>

      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24 }}>Common Questions</h2>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Can I use Sentia Tasks on mobile?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Sentia Tasks is currently optimised for desktop browsers (Chrome, Firefox, Safari, Edge). A mobile-responsive version is in development.
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>How many workspaces/lists can I create?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            There are no limits on workspaces or lists. Create as many as you need to stay organised.
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Can I invite unlimited collaborators?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Yes, you can invite as many people as needed to your lists. Collaborators don't need a paid subscription to access lists you've shared with them.
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>What happens if I delete a task by mistake?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Currently, deleted tasks cannot be recovered. An undo feature is planned for a future update. Always double-check before deleting.
          </p>
        </div>
      </section>

      <div style={{ backgroundColor: '#eff6ff', padding: 24, borderRadius: 8, border: '2px solid #3b82f6', textAlign: 'center' }}>
        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>What's Next?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginBottom: 16 }}>
          Now that you're up and running, explore these features:
        </p>
        <ul style={{ fontSize: 15, lineHeight: 1.8, color: '#475569', paddingLeft: 20, textAlign: 'left', display: 'inline-block' }}>
          <li>Create multiple lists to manage different projects</li>
          <li>Experiment with templates to discover time-saving workflows</li>
          <li>Invite your team to collaborate on shared projects</li>
          <li>Customise task details with rich descriptions and checklists</li>
          <li>Review the Done column regularly to track your progress</li>
        </ul>
        <p style={{ fontSize: 18, fontWeight: 600, color: '#1e40af', marginTop: 24, marginBottom: 0 }}>
          Welcome to Sentia Tasks – let's get productive!
        </p>
      </div>
    </div>
  );
}

/**
 * Complete User Manual - FULL CONTENT
 */
function UserManual() {
  return (
    <div className="prose" style={{ paddingBottom: 60 }}>
      <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 16 }}>Sentia Tasks: Complete User Manual</h1>
      <p style={{ fontSize: 18, color: '#64748b', marginBottom: 48 }}>Your comprehensive guide to mastering collaborative task management</p>

      {/* TABLE OF CONTENTS */}
      <section style={{ backgroundColor: '#f8fafc', padding: 24, borderRadius: 8, marginBottom: 48 }}>
        <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Table of Contents</h3>
        <ol style={{ fontSize: 15, lineHeight: 2, color: '#475569', paddingLeft: 24, margin: 0, columnCount: 2, columnGap: 32 }}>
          <li>Introduction</li>
          <li>Getting Started</li>
          <li>Workspaces</li>
          <li>Lists</li>
          <li>Tasks</li>
          <li>Collaboration</li>
          <li>Template Marketplace</li>
          <li>Best Practices</li>
          <li>Troubleshooting</li>
          <li>Frequently Asked Questions</li>
        </ol>
      </section>

      {/* 1. INTRODUCTION */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          1. Introduction
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>What is Sentia Tasks?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Sentia Tasks is a collaborative task management platform designed to help individuals, teams, and organisations organise their work effectively. Built on the proven kanban methodology, Sentia Tasks provides an intuitive interface for managing projects, tracking progress, and coordinating with teammates in real-time.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Key Features</h3>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>📋 Kanban Board Interface:</strong> Visual task management with drag-and-drop functionality across three columns: To Do, Doing, and Done</li>
          <li><strong>👥 Real-Time Collaboration:</strong> Invite teammates to lists and work together with instant synchronisation. Changes appear immediately for all collaborators</li>
          <li><strong>📝 Rich Task Details:</strong> Add detailed descriptions, checklists, and formatted content using Markdown syntax</li>
          <li><strong>🎯 Template Marketplace:</strong> Access a growing library of pre-built templates for common workflows. Choose from free templates or purchase premium templates</li>
          <li><strong>🔒 Secure & Private:</strong> Your data is encrypted and stored securely in compliance with UK data protection regulations</li>
        </ul>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Who Should Use Sentia Tasks?</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
          <div style={{ backgroundColor: '#f0fdf4', padding: 20, borderRadius: 8, border: '1px solid #86efac' }}>
            <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#166534' }}>Students</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Manage essays, assignments, and research projects</li>
              <li>Organise group coursework</li>
              <li>Track deadlines and progress</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, border: '1px solid #93c5fd' }}>
            <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>Teams</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Coordinate marketing campaigns</li>
              <li>Manage software development sprints</li>
              <li>Plan product launches</li>
              <li>Run creative projects</li>
            </ul>
          </div>

          <div style={{ backgroundColor: '#fef3c7', padding: 20, borderRadius: 8, border: '1px solid #fde047' }}>
            <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#854d0e' }}>Individuals</h4>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Organise personal goals</li>
              <li>Track habits and routines</li>
              <li>Manage household projects</li>
              <li>Plan events and travel</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 2. GETTING STARTED */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          2. Getting Started
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>System Requirements</h3>
        <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8, marginBottom: 24 }}>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginBottom: 12 }}><strong>Supported Browsers:</strong></p>
          <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li>Google Chrome (version 90+)</li>
            <li>Mozilla Firefox (version 88+)</li>
            <li>Safari (version 14+)</li>
            <li>Microsoft Edge (version 90+)</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginTop: 16, marginBottom: 8 }}><strong>Internet Connection:</strong> Required for real-time synchronisation</p>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', margin: 0 }}><strong>Display:</strong> Optimised for desktop and laptop screens (1280×720 minimum resolution recommended)</p>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Account Setup</h3>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>Registration:</strong> Navigate to the Sentia Tasks login page, enter your email address and create a password, verify your email address via the confirmation link</li>
          <li><strong>First Login:</strong> Enter your credentials, you'll be greeted with an empty workspace, follow the on-screen prompts to create your first workspace and list</li>
        </ol>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Interface Overview</h3>
        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, border: '1px solid #bfdbfe' }}>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginBottom: 12 }}><strong>Left Sidebar:</strong> Workspace selector, list of all lists within the selected workspace, New Workspace and New List buttons</p>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginBottom: 12 }}><strong>Main Area:</strong> Kanban board with three columns (To Do, Doing, Done), task cards displayed within columns, Add Task buttons at the top of each column</p>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', margin: 0 }}><strong>Top Bar:</strong> Current workspace and list name, Collaborators button (👥), Templates button (📋), User account menu</p>
        </div>
      </section>

      {/* 3. WORKSPACES */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          3. Workspaces
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>What is a Workspace?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 16 }}>
          A workspace is a container that holds multiple lists. Think of workspaces as high-level categories or contexts for different areas of your life or work.
        </p>
        <div style={{ backgroundColor: '#fef3c7', padding: 16, borderRadius: 8, border: '1px solid #fde047' }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#854d0e', marginBottom: 8 }}>Examples:</p>
          <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li>"University Projects" (containing lists for different modules or assignments)</li>
            <li>"Marketing Team" (containing lists for campaigns, content, and events)</li>
            <li>"Personal Life" (containing lists for goals, household tasks, and hobbies)</li>
          </ul>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Creating a Workspace</h3>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Click <strong>"+ New Workspace"</strong> in the left sidebar</li>
          <li>Enter a descriptive name</li>
          <li>Click <strong>"Create"</strong> or press Enter</li>
        </ol>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          The workspace appears in your sidebar and is automatically selected.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Workspace Ownership</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>
          The person who creates a workspace is the <strong>owner</strong>. Workspace owners have full control over:
        </p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Creating and deleting lists within the workspace</li>
          <li>Managing workspace settings</li>
          <li>Inviting collaborators to lists</li>
        </ul>

        <div style={{ backgroundColor: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fca5a5', marginTop: 24 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>⚠️ Deleting a Workspace</p>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#991b1b', margin: 0 }}>
            To delete a workspace: (1) Delete all lists within the workspace first, (2) Contact support to request workspace deletion. <strong>Workspace deletion is permanent and cannot be undone.</strong> All associated lists and tasks will be lost.
          </p>
        </div>
      </section>

      {/* 4. LISTS */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          4. Lists
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>What is a List?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 16 }}>
          A list represents a single project within a workspace. Each list contains tasks organised into three columns:
        </p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>To Do:</strong> Tasks not yet started</li>
          <li><strong>Doing:</strong> Tasks currently in progress</li>
          <li><strong>Done:</strong> Completed tasks</li>
        </ul>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Creating a List</h3>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Select a workspace from the sidebar</li>
          <li>Click <strong>"+ New List"</strong> at the bottom of the lists panel</li>
          <li>Enter a descriptive name (e.g., "Marketing Q1 Campaign", "Essay: Climate Change")</li>
          <li>Press Enter</li>
        </ol>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          The list opens automatically, displaying an empty kanban board.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Naming Best Practices</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ backgroundColor: '#f0fdf4', padding: 16, borderRadius: 8, border: '1px solid #86efac' }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#166534', marginBottom: 8 }}>✅ Good list names:</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>"Website Redesign Project"</li>
              <li>"Essay: The Impact of Social Media"</li>
              <li>"Home Renovation - Kitchen"</li>
              <li>"Marketing Campaign - Summer 2025"</li>
            </ul>
          </div>
          <div style={{ backgroundColor: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fca5a5' }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>❌ Poor list names:</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>"Project 1" (too vague)</li>
              <li>"Stuff" (not descriptive)</li>
              <li>"Tasks" (every list contains tasks – be specific)</li>
            </ul>
          </div>
        </div>

        <div style={{ backgroundColor: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fca5a5', marginTop: 24 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>⚠️ Deleting a List</p>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#991b1b', margin: 0 }}>
            To delete: Hover over the list name in the sidebar → Click the settings icon → Select "Delete List" → Confirm. <strong>This action is permanent.</strong> All tasks within the list will be deleted and cannot be recovered.
          </p>
        </div>
      </section>

      {/* 5. TASKS */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          5. Tasks
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>What is a Task?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          A task is a single unit of work within a list. Tasks are displayed as cards on the kanban board and move between columns as work progresses.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Creating a Task</h3>
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Method 1: Quick Add</p>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24, marginBottom: 16 }}>
          <li>Click <strong>"+ Add Task"</strong> at the top of any column</li>
          <li>Enter a task title</li>
          <li>Press Enter</li>
        </ol>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Task Properties</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>Every task has the following properties:</p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>Title (Required):</strong> Brief description of the task, appears on the task card, should be action-oriented</li>
          <li><strong>Description (Optional):</strong> Detailed information, supports Markdown formatting, can include checklists, links, and formatted text</li>
          <li><strong>Status (Automatic):</strong> Determined by which column the task is in (To Do, Doing, or Done)</li>
          <li><strong>Author (Automatic):</strong> User who created the task</li>
          <li><strong>Created Date (Automatic):</strong> Timestamp when the task was created</li>
        </ul>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Moving Tasks</h3>
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Drag and Drop:</p>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Click and hold on a task card</li>
          <li>Drag it to the desired column</li>
          <li>Release to drop</li>
        </ol>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginTop: 12 }}>
          Tasks can be moved between any columns in any order. Drag tasks vertically within a column to reorder them.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Task Descriptions with Markdown</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>
          Sentia Tasks supports Markdown formatting in task descriptions, allowing you to create rich, formatted content.
        </p>
        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8, fontFamily: 'monospace', fontSize: 13, marginTop: 12 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`# Heading 1
## Heading 2
### Heading 3

**Bold text**
*Italic text*

- Bullet point 1
- Bullet point 2

1. Numbered item 1
2. Numbered item 2

[Link text](https://example.com)

> Blockquote

\`Inline code\``}</pre>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Checklists</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>
          Checklists help you break down complex tasks into smaller, manageable steps.
        </p>
        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8, fontFamily: 'monospace', fontSize: 13 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`### Checklist
- [ ] First step
- [ ] Second step
- [ ] Third step`}</pre>
        </div>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginTop: 12 }}>
          Click the checkbox next to each item to mark it complete. Checked items appear with a strikethrough.
        </p>

        <div style={{ backgroundColor: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fca5a5', marginTop: 24 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>⚠️ Deleting a Task</p>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#991b1b', margin: 0 }}>
            Open the task by clicking on it → Click the "Delete" button → Confirm. <strong>Deleted tasks cannot be recovered.</strong> Consider moving tasks to "Done" instead of deleting them to maintain a record.
          </p>
        </div>
      </section>

      {/* 6. COLLABORATION */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          6. Collaboration
        </h2>

        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 24 }}>
          Sentia Tasks enables real-time collaboration, allowing multiple people to work on the same list simultaneously. All changes synchronise instantly across all users.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Inviting Collaborators</h3>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Open the list you want to share</li>
          <li>Click the <strong>"👥 Collaborators"</strong> button in the top-right corner</li>
          <li>Enter the email address of the person you want to invite</li>
          <li>Select their permission level:
            <ul style={{ marginTop: 8 }}>
              <li><strong>View:</strong> Can see tasks but cannot create, edit, or delete them</li>
              <li><strong>Edit:</strong> Can create, modify, move, and delete tasks</li>
            </ul>
          </li>
          <li>Click <strong>"Send Invite"</strong></li>
        </ol>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          An email invitation is sent to the recipient. They must click the link in the email to accept the invitation.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Permission Levels Explained</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div style={{ backgroundColor: '#f8fafc', padding: 16, borderRadius: 8 }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>View Permission</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>See all tasks in the list</li>
              <li>Open tasks to read descriptions</li>
              <li>View task history</li>
              <li>Cannot make any changes</li>
            </ul>
          </div>
          <div style={{ backgroundColor: '#f8fafc', padding: 16, borderRadius: 8 }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Edit Permission</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>All View permissions, plus:</li>
              <li>Create new tasks</li>
              <li>Edit task titles and descriptions</li>
              <li>Move tasks between columns</li>
              <li>Delete tasks</li>
              <li>Cannot manage collaborators</li>
            </ul>
          </div>
          <div style={{ backgroundColor: '#eff6ff', padding: 16, borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1e40af' }}>Owner</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>All Edit permissions, plus:</li>
              <li>Invite and remove collaborators</li>
              <li>Delete the list</li>
              <li>Transfer ownership (via support)</li>
            </ul>
          </div>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Real-Time Synchronisation</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>
          When collaborators are working on the same list:
        </p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>New tasks appear instantly</strong> when created by any user</li>
          <li><strong>Task movements</strong> (dragging between columns) update for everyone in real-time</li>
          <li><strong>Edits to task content</strong> synchronise automatically</li>
          <li><strong>Deleted tasks disappear immediately</strong> for all users</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', fontWeight: 600, marginTop: 16 }}>
          No manual refresh required. The interface updates automatically.
        </p>
      </section>

      {/* 7. TEMPLATE MARKETPLACE */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          7. Template Marketplace
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>What are Templates?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569' }}>
          Templates are pre-built task lists designed by experts for common workflows. Instead of creating tasks from scratch, you can deploy a template that generates multiple tasks instantly, saving time and ensuring you don't miss important steps.
        </p>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Template Types</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ backgroundColor: '#f0fdf4', padding: 20, borderRadius: 8, border: '1px solid #86efac' }}>
            <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#166534' }}>Free Templates</h4>
            <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Available to all users at no cost</li>
              <li>Cover basic, widely-applicable workflows</li>
              <li>Great for trying out the template system</li>
              <li>Examples: "Project Kickoff", "Weekly Planning"</li>
            </ul>
          </div>
          <div style={{ backgroundColor: '#fef3c7', padding: 20, borderRadius: 8, border: '1px solid #fde047' }}>
            <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#854d0e' }}>Premium Templates</h4>
            <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>One-time purchase (£7.99 to £49.99)</li>
              <li>Advanced workflows with interactive customisation</li>
              <li>Specialised for specific industries</li>
              <li>Purchase once, use unlimited times</li>
            </ul>
          </div>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Template Categories</h3>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>General:</strong> Universal templates suitable for any context</li>
          <li><strong>Academic:</strong> Templates for students (essays, group projects, research)</li>
          <li><strong>Work:</strong> Business processes (meetings, reports, project planning)</li>
          <li><strong>Personal:</strong> Life organisation (goal setting, habit tracking)</li>
          <li><strong>Marketing:</strong> Campaigns, content planning, social media</li>
          <li><strong>Development:</strong> Software projects, sprints, releases</li>
          <li><strong>Design:</strong> Creative workflows, design processes</li>
        </ul>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Applying a Template</h3>
        <ol style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Open a list where you want to add tasks</li>
          <li>Click the <strong>"📋 Templates"</strong> button in the toolbar</li>
          <li>Browse and select a template</li>
          <li>Review the preview</li>
          <li>Click <strong>"Use this template"</strong></li>
          <li>If it's an interactive template, fill in the customisation form</li>
          <li>Click <strong>"Deploy"</strong></li>
        </ol>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Interactive Templates</h3>
        <p style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', marginBottom: 12 }}>
          Interactive templates prompt you to fill in details before deployment:
        </p>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li><strong>Text Input:</strong> Enter names, descriptions, titles</li>
          <li><strong>Number Input:</strong> Enter quantities, budgets, team sizes</li>
          <li><strong>Date Input:</strong> Select dates from a calendar picker</li>
          <li><strong>Dropdown:</strong> Choose one option from a list</li>
          <li><strong>Checkbox:</strong> Yes/No toggle</li>
          <li><strong>Checkbox Group:</strong> Select multiple options</li>
        </ul>

        <div style={{ backgroundColor: '#eff6ff', padding: 20, borderRadius: 8, border: '1px solid #bfdbfe', marginTop: 24 }}>
          <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>Purchasing Premium Templates</h4>
          <ol style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li>Select a premium template from the gallery</li>
            <li>Review the template details and price</li>
            <li>Click "Purchase Template"</li>
            <li>Complete payment via secure Stripe checkout</li>
            <li>Once purchased, the template appears in your library permanently</li>
          </ol>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', marginTop: 12, marginBottom: 0 }}>
            <strong>You can use purchased templates unlimited times across any of your lists.</strong>
          </p>
        </div>
      </section>

      {/* 8. BEST PRACTICES */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          8. Best Practices
        </h2>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Writing Effective Task Titles</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ backgroundColor: '#f0fdf4', padding: 16, borderRadius: 8, border: '1px solid #86efac' }}>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#166534', marginBottom: 8 }}>✅ Do:</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Start with action verbs (Create, Write, Review)</li>
              <li>Be specific ("Write introduction for climate essay")</li>
              <li>Keep titles concise (under 60 characters)</li>
            </ul>
          </div>
          <div style={{ backgroundColor: '#fef2f2', padding: 16, borderRadius: 8, border: '1px solid #fca5a5' }}>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>❌ Don't:</p>
            <ul style={{ fontSize: 14, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
              <li>Use vague titles ("Do stuff", "Work on project")</li>
              <li>Include too much detail (save for description)</li>
              <li>Use all capitals (LIKE THIS)</li>
            </ul>
          </div>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Column Usage</h3>
        <div style={{ backgroundColor: '#f8fafc', padding: 20, borderRadius: 8 }}>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}><strong>To Do:</strong></p>
          <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, marginBottom: 16 }}>
            <li>Tasks you plan to work on but haven't started</li>
            <li>Keep this column relatively short (under 20 tasks)</li>
            <li>Prioritise by placing urgent tasks at the top</li>
          </ul>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}><strong>Doing:</strong></p>
          <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, marginBottom: 16 }}>
            <li>Tasks you're actively working on right now</li>
            <li>Limit to 2-3 tasks per person to maintain focus</li>
            <li>Move tasks here only when you begin work</li>
          </ul>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}><strong>Done:</strong></p>
          <ul style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', paddingLeft: 20, margin: 0 }}>
            <li>Completed tasks</li>
            <li>Review this column weekly to celebrate progress</li>
            <li>Archive or delete old tasks to prevent clutter</li>
          </ul>
        </div>

        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, marginTop: 32 }}>Collaboration Best Practices</h3>
        <ul style={{ fontSize: 16, lineHeight: 1.8, color: '#475569', paddingLeft: 24 }}>
          <li>Use task descriptions to add context for teammates</li>
          <li>Add checklists to break down complex tasks</li>
          <li>Use the "Doing" column to show who's working on what</li>
          <li>Agree on conventions with your team (e.g., maximum 3 tasks in "Doing" per person)</li>
          <li>Hold weekly reviews to move tasks and update progress</li>
        </ul>
      </section>

      {/* 9. TROUBLESHOOTING */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          9. Troubleshooting
        </h2>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Tasks Not Appearing</h4>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}><strong>Possible causes:</strong> Viewing wrong list, synchronisation delay, browser caching</p>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 0 }}><strong>Solutions:</strong> Check correct list is selected, refresh the page (Ctrl+R / Cmd+R), clear browser cache, check internet connection</p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Cannot Invite Collaborators</h4>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}><strong>Possible causes:</strong> You're not the list owner, invalid email address, recipient already has access</p>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 0 }}><strong>Solutions:</strong> Verify you created the list (only owners can invite), double-check email address for typos, check Collaborators panel to see if they're already added</p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Template Deployment Failed</h4>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}><strong>Possible causes:</strong> Network connectivity issue, required fields not filled in, browser compatibility</p>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 0 }}><strong>Solutions:</strong> Check internet connection, ensure all required fields are completed, try a different browser</p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <h4 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Drag and Drop Not Working</h4>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}><strong>Possible causes:</strong> Browser compatibility, slow internet, too many tasks</p>
          <p style={{ fontSize: 15, color: '#475569', marginBottom: 0 }}><strong>Solutions:</strong> Refresh the page, use a supported browser (Chrome, Firefox, Safari, Edge), check internet speed, archive old tasks</p>
        </div>
      </section>

      {/* 10. FAQ */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
          10. Frequently Asked Questions
        </h2>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Can I use Sentia Tasks on mobile devices?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Sentia Tasks is currently optimised for desktop browsers. A mobile-responsive version is under development.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Is there a limit to workspaces or lists?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            No, there are no limits. Create as many as you need to stay organised.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Can I invite unlimited collaborators?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Yes, there's no limit on collaborators. Invited users can access shared lists without a subscription.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Can I recover deleted tasks?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            No, deleted tasks cannot be recovered. Consider moving tasks to "Done" instead of deleting them.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Can I attach files to tasks?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            File attachments are not currently supported. You can add links to cloud storage (Google Drive, Dropbox) in task descriptions.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>How many times can I use a template after purchasing it?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Unlimited. Purchase once, deploy as many times as you want.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Can I share purchased templates with my team?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Purchases are per-user. If you deploy a template to a shared workspace, collaborators see the tasks, but they can't deploy the template themselves unless they also purchase it.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Where is my data stored?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            All data is stored on encrypted cloud servers in compliance with UK data protection regulations (GDPR).
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Who can see my tasks?</h4>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', margin: 0 }}>
            Only you and collaborators you've explicitly invited can see tasks in your lists. Sentia staff do not access user data except for technical support with your consent.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <div style={{ backgroundColor: '#eff6ff', padding: 24, borderRadius: 8, border: '2px solid #3b82f6', textAlign: 'center', marginTop: 48 }}>
        <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#1e40af' }}>Need More Help?</h3>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: '#475569', marginBottom: 16 }}>
          Email: <strong>support@sentia-app.com</strong>
        </p>
        <p style={{ fontSize: 15, color: '#64748b', margin: 0 }}>
          Response time: Within 24 hours on business days
        </p>
      </div>
    </div>
  );
}
